/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import type { Dispatch } from '@reduxjs/toolkit';

import { type RootState } from '../../../services/serviceTypes';
import {
  MESSAGE_SUBTYPES,
  NAVIGATION_STEPS,
  STEP_KEYS,
  STEP_NAMES,
} from '../constants/steps';
import {
  addMessage,
  addMessageToEntitlement,
  removeMessagesBySubType,
  setActiveStep,
  setSavedStepData,
} from '../data-store/copilotSlice';
import { createFormMessage } from '../services/messageTemplatesService';

import { isEditMode } from './modeUtils';

export const showGeneralForm = (
  dispatch: Dispatch,
  setActive = true,
  formData: Record<string, unknown> = {},
  getState?: () => unknown
) => {
  // Check if we're still in General step before showing the form
  if (getState && setActive) {
    const state = getState() as {
      copilot?: {
        ui?: { activeStep?: string };
        messages?: { subType?: string; stepName?: string }[];
      };
    };
    const currentActiveStep = state.copilot?.ui?.activeStep;
    const messages = state.copilot?.messages || [];

    // Guard: Check if General form already exists
    const hasGeneralForm = messages.some(
      (msg) => msg.stepName === 'General' && msg.subType === 'basic_config'
    );

    if (hasGeneralForm) {
      return; // Don't add General form if it already exists
    }

    // Check if there are any AddAccess messages, indicating we're in AddAccess flow
    const hasAddAccessMessages = messages.some(
      (msg) => msg.subType === 'add_access_form'
    );

    if (hasAddAccessMessages) {
      return;
    }

    if (
      currentActiveStep &&
      currentActiveStep !== 'General' &&
      currentActiveStep !== undefined
    ) {
      return; // Don't show General form if we've navigated to another step
    }
  }

  let completedSteps: string[] = [];

  if (getState) {
    const state = getState() as {
      copilot?: { navigation?: { completedSteps?: string[] } };
    };

    completedSteps = state.copilot?.navigation?.completedSteps || [];
  }

  const formMessage = createFormMessage(STEP_KEYS.GENERAL, {
    heading: NAVIGATION_STEPS.find((s) => s.key === STEP_KEYS.GENERAL)?.label,
    formData, // Pass existing form data for pre-filling
    completedSteps,
  });

  dispatch(addMessage(formMessage));

  if (setActive) {
    dispatch(setActiveStep(STEP_NAMES.GENERAL));

    // Set initial baseline for save draft functionality in create mode
    if (getState && !isEditMode(getState)) {
      const state = getState() as {
        copilot?: { stepFormData?: { General?: Record<string, unknown> } };
      };
      const currentGeneralData = state.copilot?.stepFormData?.General || {};

      // Set saved step data to establish baseline for unsaved changes detection
      dispatch(
        setSavedStepData({
          stepName: 'General',
          formData: currentGeneralData,
        })
      );
    }
  }
};

export const showAccountForm = (
  dispatch: Dispatch,
  stepKey: keyof typeof STEP_KEYS,
  stepName: string,
  isInEditMode = false,
  formData: Record<string, unknown> = {},
  getState?: () => unknown
) => {
  let completedSteps: string[] = [];

  if (getState) {
    const state = getState() as {
      copilot?: { navigation?: { completedSteps?: string[] } };
    };

    completedSteps = state.copilot?.navigation?.completedSteps || [];
  }

  const formMessage = createFormMessage(
    STEP_KEYS[stepKey] as
      | 'GENERAL'
      | 'CONNECTION'
      | 'CREATE_ACCOUNT'
      | 'UPDATE_ACCOUNT'
      | 'ENABLE_ACCOUNT'
      | 'DISABLE_ACCOUNT'
      | 'REMOVE_ACCOUNT',
    {
      heading: NAVIGATION_STEPS.find((s) => s.key === STEP_KEYS[stepKey])
        ?.label,
      formData,
      isEditMode: isInEditMode,
      completedSteps,
    }
  );

  dispatch(addMessage(formMessage));

  if (isInEditMode) {
    dispatch(setActiveStep(stepName));
  }
};

export const showConnectionForm = (
  dispatch: Dispatch,
  getState?: () => unknown
) => {
  // Check if connection form already exists in messages
  if (getState) {
    const state = getState() as {
      copilot?: {
        messages?: {
          type?: string;
          subType?: string;
          stepName?: string;
        }[];
        parsedTemplates?: Record<string, unknown>;
        navigation?: { completedSteps?: string[] };
      };
    };

    const messages = state.copilot?.messages || [];
    const existingConnectionForm = messages.find(
      (msg) => msg.type === 'step_form' && msg.stepName === 'Connection'
    );

    // If connection form already exists, don't create a new one
    if (existingConnectionForm) {
      return;
    }
  }

  dispatch(removeMessagesBySubType(MESSAGE_SUBTYPES.CONNECTION_CONFIG));

  // Get parsed templates from state
  let fields: unknown[] = [];
  let completedSteps: string[] = [];

  if (getState) {
    const state = getState() as {
      copilot?: {
        parsedTemplates?: Record<string, unknown>;
        navigation?: { completedSteps?: string[] };
      };
    };

    fields = (state.copilot?.parsedTemplates?.Connection ?? []) as unknown[];
    completedSteps = state.copilot?.navigation?.completedSteps || [];
  }

  const formMessage = createFormMessage(STEP_KEYS.CONNECTION, {
    heading: NAVIGATION_STEPS.find((s) => s.key === STEP_KEYS.CONNECTION)
      ?.label,
    fields,
    completedSteps,
  });

  dispatch(addMessage(formMessage));

  // Set initial baseline for save draft functionality in create mode
  if (getState && !isEditMode(getState)) {
    const state = getState() as {
      copilot?: { stepFormData?: { Connection?: Record<string, unknown> } };
    };
    const currentConnectionData = state.copilot?.stepFormData?.Connection || {};

    // Set saved step data to establish baseline for unsaved changes detection
    dispatch(
      setSavedStepData({
        stepName: 'Connection',
        formData: currentConnectionData,
      })
    );
  }
};

export const showUpdateAccountForm = (
  dispatch: Dispatch,
  getState?: () => unknown
) => {
  dispatch(removeMessagesBySubType(MESSAGE_SUBTYPES.UPDATE_ACCOUNT));

  // Get parsed templates from state
  let fields: unknown[] = [];

  if (getState) {
    const state = getState() as {
      copilot?: { parsedTemplates?: Record<string, unknown> };
    };

    fields = (state.copilot?.parsedTemplates?.UpdateAccount ?? []) as unknown[];
  }

  const formMessage = createFormMessage(STEP_KEYS.UPDATE_ACCOUNT, {
    heading: NAVIGATION_STEPS.find((s) => s.key === STEP_KEYS.UPDATE_ACCOUNT)
      ?.label,
    fields,
  });

  dispatch(addMessage(formMessage));
};

export const showCreateAccountForm = (
  dispatch: Dispatch,
  getState?: () => unknown
) => {
  dispatch(removeMessagesBySubType(MESSAGE_SUBTYPES.CREATE_ACCOUNT_FORM));

  // Get parsed templates from state
  let fields: unknown[] = [];

  if (getState) {
    const state = getState() as {
      copilot?: { parsedTemplates?: Record<string, unknown> };
    };

    fields = (state.copilot?.parsedTemplates?.CreateAccount ?? []) as unknown[];
  }

  const formMessage = createFormMessage(STEP_KEYS.CREATE_ACCOUNT, {
    heading: NAVIGATION_STEPS.find((s) => s.key === STEP_KEYS.CREATE_ACCOUNT)
      ?.label,
    fields,
  });

  dispatch(addMessage(formMessage));
};

export const showAddAccessForm = (dispatch: Dispatch, setActive = true) => {
  const formMessage = createFormMessage(STEP_KEYS.ADD_ACCESS, {
    heading: NAVIGATION_STEPS.find((s) => s.key === STEP_KEYS.ADD_ACCESS)
      ?.label,
  });

  // Add correct entitlement indices for AddAccess input form
  const formMessageWithIndices = {
    ...formMessage,
    addAccessEntitlementIndex: -1, // Input form should have -1
    removeAccessEntitlementIndex: -1,
  };

  dispatch(addMessage(formMessageWithIndices));

  if (setActive) {
    dispatch(setActiveStep(STEP_NAMES.ADD_ACCESS));
  }
};

export const showRemoveAccessForm = (
  dispatch: Dispatch,
  getState?: () => unknown,
  setActive = true
) => {
  // Get parsed templates from state
  let fields: unknown[] = [];

  if (getState) {
    const state = getState() as RootState;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const removeAccessTemplate = state.copilot.parsedTemplates.RemoveAccess;

    if (removeAccessTemplate) {
      fields = [
        {
          ...removeAccessTemplate,
          apiURL: removeAccessTemplate?.apiURL || '/api/v23/getTableAttributes',
          domainObjects: removeAccessTemplate?.domainObjects,
        },
      ];
    }
  }

  const formMessage = createFormMessage(STEP_KEYS.REMOVE_ACCESS, {
    heading: NAVIGATION_STEPS.find((s) => s.key === STEP_KEYS.REMOVE_ACCESS)
      ?.label,
    fields,
  });

  // Always use addMessageToEntitlement for proper message grouping
  dispatch(
    addMessageToEntitlement({
      entitlementIndex: 0, // First entitlement
      entitlementType: 'removeAccess',
      message: formMessage,
    })
  );

  if (setActive) {
    dispatch(setActiveStep(STEP_NAMES.REMOVE_ACCESS));
  }
};
